import { useState } from 'react';
import { Icon } from 'semantic-ui-react';
import {
  useGetPayoutStatusQuery,
  useSetAutoPayoutMutation,
  useStartPayoutOnboardingMutation
} from '../../redux/apis/endpoints/earningsApi';
import './PayoutSettings.css';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

/**
 * Getting paid: connect an account, switch on monthly payouts, and see exactly
 * what's still standing between you and the money.
 *
 * The server decides eligibility; this only renders its reasons, so the UI can
 * never imply someone will be paid when the payout gate says otherwise.
 */
const PayoutSettings = () => {
  const { data: status, isLoading, isError, refetch } = useGetPayoutStatusQuery();
  const [setAutoPayout, { isLoading: isSaving }] = useSetAutoPayoutMutation();
  const [startOnboarding, { isLoading: isStarting }] = useStartPayoutOnboardingMutation();
  const [error, setError] = useState(null);

  if (isLoading) {
    return <div className="payout-settings loading">Loading payout settings…</div>;
  }

  if (isError || !status) {
    return (
      <div className="payout-settings">
        <div className="payout-settings-title">Getting paid</div>
        <button className="payout-connect-btn" onClick={refetch}>
          <Icon name="refresh" /> Retry loading payout settings
        </button>
      </div>
    );
  }

  const {
    balanceUsd = 0,
    thresholdUsd = 0,
    onboarded,
    payoutsEnabled,
    autoPayoutMonthly,
    eligible
  } = status;

  const progress = thresholdUsd > 0 ? Math.min((balanceUsd / thresholdUsd) * 100, 100) : 0;

  const handleConnect = async () => {
    setError(null);
    try {
      const { url } = await startOnboarding().unwrap();
      if (url) window.location.href = url;
      else setError('Could not start onboarding. Try again.');
    } catch (err) {
      setError(err?.data?.message || 'Could not start onboarding. Try again.');
    }
  };

  const handleToggle = async () => {
    setError(null);
    try {
      await setAutoPayout(!autoPayoutMonthly).unwrap();
    } catch (err) {
      setError(err?.data?.message || 'Could not update payout setting.');
    }
  };

  return (
    <div className="payout-settings">
      <div className="payout-settings-title">Getting paid</div>

      {/* Progress toward the payout threshold */}
      <div className="payout-progress-row">
        <span className="payout-progress-label">
          <strong>{money(balanceUsd)}</strong> of {money(thresholdUsd)} minimum
        </span>
        {eligible && <span className="payout-ready-pill">Ready</span>}
      </div>
      <div className="payout-progress-track">
        <div className="payout-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* Step 1 — connect an account */}
      {!onboarded ? (
        <button className="payout-connect-btn" onClick={handleConnect} disabled={isStarting}>
          {isStarting ? <Icon name="spinner" loading /> : <Icon name="university" />}
          Set up payouts
        </button>
      ) : !payoutsEnabled ? (
        <div className="payout-notice warning">
          <Icon name="exclamation triangle" />
          <div>
            <strong>Finish verifying your account</strong>
            <span>Stripe still needs a few details before it can pay you.</span>
          </div>
          <button className="payout-inline-btn" onClick={handleConnect} disabled={isStarting}>
            Continue
          </button>
        </div>
      ) : (
        <div className="payout-notice ok">
          <Icon name="check circle" />
          <div>
            <strong>Payout account connected</strong>
            <span>Verified and ready to receive transfers.</span>
          </div>
        </div>
      )}

      {/* Step 2 — opt in to monthly payouts */}
      <button
        className={`payout-toggle-row ${autoPayoutMonthly ? 'on' : ''}`}
        onClick={handleToggle}
        disabled={isSaving || !onboarded}
        type="button"
        role="switch"
        aria-checked={!!autoPayoutMonthly}
      >
        <span className="payout-toggle-copy">
          <span className="payout-toggle-title">Pay me monthly</span>
          <span className="payout-toggle-desc">
            {onboarded
              ? 'Transfers your balance to your account once a month, once it clears the minimum.'
              : 'Connect a payout account first.'}
          </span>
        </span>
        <span className="payout-switch">
          <span className="payout-switch-knob" />
        </span>
      </button>

      {error && <div className="payout-error">{error}</div>}
    </div>
  );
};

export default PayoutSettings;
